import { useMemo, useState } from 'react';
import { Button, IconButton, Tooltip } from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import SettingsIcon from '@mui/icons-material/Settings';
import InfoButton from '../../../components/InfoButton';
import ExtensionSettingsDialog from './ExtensionSettingsDialog';
import ExtensionSuggestionsDialog from './ExtensionSuggestionsDialog';
import { useDismissedExtensionSuggestions } from '../hooks/useDismissedExtensionSuggestions';
import type { RentalExtensionSuggestion } from '../../planning/types';
import type { ExtensionSettings } from '../types';
import { useNotification } from '../../../context/useNotification';
import { DASHBOARD_LABELS as LABELS } from '../../../utils/labels';

interface ExtensionSuggestionCardProps {
  suggestions: RentalExtensionSuggestion[];
  onNavigate: (machineIds: string[]) => void;
  settings: ExtensionSettings;
  onSettingsChange: (settings: ExtensionSettings) => void;
  userId: string;
}

export default function ExtensionSuggestionCard({
  suggestions,
  onNavigate,
  settings,
  onSettingsChange,
  userId,
}: ExtensionSuggestionCardProps) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const { dismissedIds, dismiss, reset } = useDismissedExtensionSuggestions(userId);
  const { notifySuccess, notifyError } = useNotification();

  const visibleSuggestions = useMemo(
    () => suggestions.filter((s) => !dismissedIds.has(s.anchor.id)),
    [suggestions, dismissedIds]
  );

  const handleResetDismissed = () => {
    reset().then((ok) => {
      if (ok) notifySuccess(LABELS.extensionResetSuccess); else notifyError(LABELS.extensionResetError);
    });
  };

  const handleDismiss = (anchorMachineId: string) => {
    dismiss(anchorMachineId).then((ok) => {
      if (ok) notifySuccess(LABELS.extensionDismissSuccess); else notifyError(LABELS.extensionDismissError);
    });
  };

  return (
    <div className="bg-surface border border-border rounded-xl p-5 flex flex-col gap-1">
      <div className="flex items-center gap-0.5">
        <h2 className="text-base flex items-center gap-2 font-semibold">
          <AutorenewIcon color="primary" />
          {LABELS.extensionTitle}
        </h2>
        <InfoButton title={LABELS.extensionTitle} description={LABELS.infoExtension} />
        <Tooltip title={LABELS.extensionSettingsButton}>
          <IconButton
            size="small"
            className="ml-auto"
            onClick={() => setSettingsOpen(true)}
            aria-label={LABELS.extensionSettingsButton}
            data-testid="extension-settings-button"
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      {settingsOpen && (
        <ExtensionSettingsDialog
          settings={settings}
          onClose={() => setSettingsOpen(false)}
          onSave={onSettingsChange}
          hasDismissed={dismissedIds.size > 0}
          onResetDismissed={handleResetDismissed}
        />
      )}

      {viewAllOpen && (
        <ExtensionSuggestionsDialog
          suggestions={visibleSuggestions}
          onNavigate={onNavigate}
          onDismiss={handleDismiss}
          onClose={() => setViewAllOpen(false)}
        />
      )}

      {visibleSuggestions.length === 0 ? (
        <p className="text-sm text-text-secondary mt-0 mb-0">{LABELS.extensionEmpty}</p>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-text-secondary mt-0 mb-0">{LABELS.extensionSummary(visibleSuggestions.length)}</p>
          <Button
            variant="text"
            size="small"
            onClick={() => setViewAllOpen(true)}
            data-testid="view-all-suggestions-button"
          >
            {LABELS.extensionViewAll}
          </Button>
        </div>
      )}
    </div>
  );
}
